import { Injectable } from '@angular/core';
import { IChatConversationMessage } from '@fi-shared/fi-chat/fi-chat';
import { Observable, Subject } from 'rxjs';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { FiImageSize } from '@fi-shared/types';
import { FiEmojiService } from '@fi-shared/fi-emoji-button/fi-emoji.service';
import { FiMediaStorageService } from '@fi-shared/media-bank/fi-media-storage.service';

export interface IChatConversationMessageActionsData {
    conversationId: number;
    conversationMessage: IChatConversationMessage;
}

@Injectable()
export class FiChatConversationMessageActionsService {
    private _conversationMessageEditClick$ = new Subject<IChatConversationMessageActionsData>();
    private _conversationMessageReply$ = new Subject<IChatConversationMessageActionsData>();

    constructor(
        private fiMediaStorageService: FiMediaStorageService,
        private fiEmojiService: FiEmojiService,
    ) {}

    private _conversationMessageDelete$ = new Subject<IChatConversationMessageActionsData>();

    get conversationMessageDelete$(): Observable<IChatConversationMessageActionsData> {
        return this._conversationMessageDelete$.asObservable();
    }

    private _conversationMessageDeleteForUser$ = new Subject<IChatConversationMessageActionsData>();

    get conversationMessageDeleteForUser$(): Observable<IChatConversationMessageActionsData> {
        return this._conversationMessageDeleteForUser$.asObservable();
    }

    get conversationMessageEdit$(): Observable<IChatConversationMessageActionsData> {
        return this._conversationMessageEditClick$.asObservable();
    }

    get conversationMessageReplyEdit$(): Observable<IChatConversationMessageActionsData> {
        return this._conversationMessageReply$.asObservable();
    }

    private _conversationMessageForward$ = new Subject<IChatConversationMessageActionsData>();

    get conversationMessageForward$(): Observable<IChatConversationMessageActionsData> {
        return this._conversationMessageForward$.asObservable();
    }

    delete(conversationId: number, conversationMessage: IChatConversationMessage): void {
        this._conversationMessageDelete$.next({ conversationId, conversationMessage });
    }

    deleteForUser(conversationId: number, conversationMessage: IChatConversationMessage): void {
        this._conversationMessageDeleteForUser$.next({ conversationId, conversationMessage });
    }

    edit(conversationId: number, conversationMessage: IChatConversationMessage): void {
        this._conversationMessageEditClick$.next({ conversationId, conversationMessage });
    }

    reply(conversationId: number, conversationMessage: IChatConversationMessage): void {
        this._conversationMessageReply$.next({ conversationId, conversationMessage });
    }

    forward(conversationId: number, conversationMessage: IChatConversationMessage): void {
        this._conversationMessageForward$.next({ conversationId, conversationMessage });
    }

    async copy(conversationMessage: IChatConversationMessage) {
        if (conversationMessage.Content?.length) {
            await this.copyConversationMessageContent(conversationMessage.Content);
        } else if (conversationMessage.MediaBankFileReferences?.length) {
            await this.copyConversationMessageMediaBank(conversationMessage.MediaBankFileReferences[0]);
        }
    }

    private getTextFromContainer(childNodes: Node[]): string {
        let copyText: string = '';
        childNodes.forEach((childNode) => {
            copyText = copyText.concat(
                childNode.nodeName === 'SPAN' || childNode.nodeName === 'DIV'
                    ? this.getTextFromContainer(Array.from(childNode.childNodes))
                    : this.getTextContentFromNode(childNode),
            );
        });
        return copyText;
    }

    private getTextContentFromNode(node: Node): string {
        if (node.nodeName === 'IMG') return this.fiEmojiService.parseEmojiToText(node as HTMLElement) + ' ';

        if (node.nodeType === Node.TEXT_NODE) return node.nodeValue + ' ';

        if (node.nodeName === 'A') return (node as HTMLAnchorElement).href + ' ';

        if (node.nodeName === 'BR') return '\n';

        return '';
    }

    private async copyConversationMessageContent(messageContent: string) {
        try {
            const divEl = document.createElement('div');
            divEl.innerHTML = messageContent;
            const copyText: string = this.getTextFromContainer(Array.from(divEl.childNodes));
            copyText.trim();
            await navigator.clipboard.writeText(copyText);
        } catch (err) {
            console.error('Failed to copy text: ', err);
        }
    }

    private async copyConversationMessageMediaBank(mediaBankFileRef: IMediaBankFileReference) {
        try {
            const image = document.createElement('img');
            const loadImagePromise = new Promise<void>((resolve, reject) => {
                image.onload = (evt) => resolve();
                image.onerror = (evt) => reject();
            });
            image.crossOrigin = 'Anonymous';
            image.src = this.fiMediaStorageService.getMediaFileUrl(mediaBankFileRef, { size: FiImageSize.Large });

            await loadImagePromise;

            const canvas = document.createElement('canvas');

            canvas.width = image.naturalWidth;
            canvas.height = image.naturalHeight;

            const ctx = canvas.getContext('2d');
            if (!ctx) throw new Error('Canvas context not supported');

            ctx.drawImage(image, 0, 0);
            canvas.toBlob(async (blob) => {
                if (!blob) throw new Error('Failed to create blob from image');

                const clipboardItem = new ClipboardItem({ 'image/png': blob });
                await navigator.clipboard.write([clipboardItem]);
            }, 'image/png');
        } catch (err) {
            console.error('Failed to copy image: ', err);
        }
    }
}
